/**
 * Procedural asset documents: ships, items, and vehicles authored in the asset
 * lab as a scene graph of bounded primitives with non-destructive modifiers,
 * named sockets, a material palette, and a kind-specific gameplay block.
 *
 * Schema-first: types + caps + validation live here so the server and every
 * client agree on what a saved asset may contain. Geometry synthesis is
 * client-side (proceduralAssetView); the deterministic flattening of the node
 * tree lives in proceduralAssetCompile.ts.
 */
import type {
  VehicleBoostDef,
  VehicleColliderDef,
  VehicleDriveKind,
  VehicleHandlingDef,
  VehicleHoverDef,
  VehicleProbeDef,
  VehicleRiderPoseDef,
  VehicleSeatRole,
  VehicleWeaponDef,
} from "./vehicles.js";
import type { AmmoType, ItemKind } from "./rpg.js";

export const ASSET_KINDS = ["ship", "item", "vehicle", "buildingModule", "buildPiece"] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

export const ASSET_NODE_KINDS = [
  "box", "cylinder", "sphere", "cone", "capsule", "torus", "lathe", "group", "socket",
] as const;
export type AssetNodeKind = (typeof ASSET_NODE_KINDS)[number];

export const ASSET_MAX_NODES = 256;
export const ASSET_MAX_INSTANCES = 2048;
export const ASSET_MAX_ARRAY_COUNT = 32;
export const ASSET_MAX_PROFILE_POINTS = 24;
export const ASSET_MAX_MATERIALS = 16;
export const ASSET_MAX_MODIFIERS = 8;
export const ASSET_POS_BOUND = 32;
export const ASSET_DIM_MIN = 0.01;
export const ASSET_DIM_MAX = 32;
export const ASSET_SCALE_MIN = 0.05;
export const ASSET_SCALE_MAX = 20;

/** params[] length per renderable primitive. Meaning, in order:
 * box [width, height, depth] · cylinder [radiusTop, radiusBottom, height] ·
 * sphere [radius] · cone [radius, height] · capsule [radius, length] ·
 * torus [radius, tube] · lathe [segments (integer 3..48)] + required profile. */
export const PRIMITIVE_PARAM_COUNTS: Readonly<Partial<Record<AssetNodeKind, number>>> = {
  box: 3, cylinder: 3, sphere: 1, cone: 2, capsule: 2, torus: 2, lathe: 1,
};

export type AssetVec3 = [number, number, number];

export type AssetModifier =
  | { type: "mirror"; axis: "x" | "y" | "z" }
  | { type: "array"; mode: "linear"; count: number; offset: AssetVec3 }
  | { type: "array"; mode: "radial"; count: number; axis: "x" | "y" | "z"; radius: number; sweep?: number }
  | { type: "taper"; axis: "x" | "y" | "z"; factor: number }
  | { type: "bend"; axis: "x" | "y" | "z"; angle: number };

export interface AssetMaterial {
  color: number;
  roughness: number;
  metalness: number;
  emissive?: number;
  emissiveIntensity?: number;
  opacity?: number;
}

export interface AssetNode {
  /** Unique within the asset. */
  id: string;
  /** Outliner label; REQUIRED (and unique post-expansion) for socket nodes. */
  name?: string;
  kind: AssetNodeKind;
  params?: number[];
  /** Lathe only: 2D profile [radius, y] points, radius >= 0. */
  profile?: [number, number][];
  /** Lathe only: 0..1 ring staggering for an angular, triangulated mineral surface. */
  faceting?: number;
  p: AssetVec3;
  /** Euler XYZ radians. */
  r: AssetVec3;
  s?: AssetVec3;
  /** Index into materials. Renderable primitives only. */
  material?: number;
  /** Applied in the node's local frame to the node + its subtree, in order.
   * mirror/array multiply instances; taper/bend deform vertices client-side. */
  modifiers?: AssetModifier[];
  children?: AssetNode[];
}

export interface AssetVehicleHardpoint {
  /** Socket name providing position + direction (socket local -Z, post-compile). */
  socket: string;
  kind: "weapon" | "utility";
  group?: 1 | 2;
  weapon?: string;
}

export interface AssetVehicleSeat {
  id: string;
  role: VehicleSeatRole;
  seatSocket: string;
  exitSocket: string;
  entrySocket?: string;
  mountDuration?: number;
  dismountDuration?: number;
  arcHeight?: number;
  hardpoints?: readonly string[];
  riderPose?: VehicleRiderPoseDef;
}

export interface AssetVehicleData {
  drive: VehicleDriveKind;
  mass: number;
  inertia: AssetVec3;
  probes: VehicleProbeDef[];
  handling: VehicleHandlingDef;
  hover?: VehicleHoverDef;
  boost?: VehicleBoostDef;
  collider: VehicleColliderDef;
  camera: { distance: number; height: number; lookAhead: number; baseFov: number; speedFov: number };
  seats: AssetVehicleSeat[];
  hardpoints: AssetVehicleHardpoint[];
  weapons: VehicleWeaponDef[];
}

export interface AssetItemData {
  kind: ItemKind;
  stack: number;
  desc: string;
  ammo?: AmmoType;
  heal?: number;
  shield?: number;
}

export interface AssetShipData {
  /** Fire group per weapon socket name. */
  weaponGroups: Record<string, 1 | 2>;
}

/** Building-kit module block: footprint on the settlement module grid (units of
 *  MODULE_UNIT_M). Socket names in buildingModule docs are TYPED by prefix. */
export interface AssetBuildingModuleData {
  footprintUnits: [number, number];
}
export const BUILDING_SOCKET_PREFIXES = ["roof:", "wall:", "ground:", "corner:"] as const;

/** Build-piece block: one atomic, player-placed building piece (the Valheim-style
 *  homestead catalog). `group` is the semantic category that drives snap rules and
 *  build-palette grouping; the piece's snap points are `socket` nodes TYPED by
 *  prefix. Material cost/tier live in the homestead piece catalog, NOT here — this
 *  block is only the geometry's intrinsic gameplay metadata. */
// "furniture" (2026-08-02, settlement-loop P0): interior/station pieces. They
// carry NO sockets — free-placed, never weld-graph members; support = resting
// on a floor (support.ts furniture rule), not socket coincidence.
export const BUILD_PIECE_GROUPS = ["foundation", "floor", "wall", "roof", "transit", "structure", "furniture"] as const;
export type BuildPieceGroup = (typeof BUILD_PIECE_GROUPS)[number];
export interface AssetBuildPieceData {
  group: BuildPieceGroup;
}
export const BUILD_PIECE_SOCKET_PREFIXES = ["edge:", "corner:", "top:", "ground:"] as const;

export interface ProceduralAssetV1 {
  version: 1;
  id: string;
  name: string;
  kind: AssetKind;
  tags: string[];
  materials: AssetMaterial[];
  nodes: AssetNode[];
  vehicle?: AssetVehicleData;
  item?: AssetItemData;
  ship?: AssetShipData;
  buildingModule?: AssetBuildingModuleData;
  buildPiece?: AssetBuildPieceData;
}

const SLUG = /^[a-z0-9][a-z0-9._-]*$/;

function finite(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}
function vec3In(v: unknown, bound: number): v is AssetVec3 {
  return Array.isArray(v) && v.length === 3 && v.every((n) => finite(n) && Math.abs(n) <= bound);
}

/** How many instances one node contributes after its own modifier stack. */
function nodeMultiplier(node: AssetNode): number {
  let m = 1;
  for (const mod of node.modifiers ?? []) {
    if (mod.type === "mirror") m *= 2;
    else if (mod.type === "array") m *= Math.max(1, mod.count);
  }
  return m;
}

function walk(nodes: AssetNode[], visit: (node: AssetNode, multiplier: number) => void, multiplier = 1): void {
  for (const node of nodes) {
    const m = multiplier * nodeMultiplier(node);
    visit(node, m);
    if (node.children) walk(node.children, visit, m);
  }
}

/** Socket names the compiler will emit, including modifier-generated copies:
 * mirror appends `<name>-mirrored`, an array of count n appends `<name>-2`..`<name>-n`
 * (compounding across stacked modifiers and ancestors). */
export function assetSocketNames(asset: ProceduralAssetV1): Set<string> {
  const names = new Set<string>();
  const expandChain = (nodes: AssetNode[], suffixes: string[]): void => {
    for (const node of nodes) {
      let variants = [""];
      for (const mod of node.modifiers ?? []) {
        if (mod.type === "mirror") variants = variants.flatMap((v) => [v, `${v}-mirrored`]);
        else if (mod.type === "array") {
          const next: string[] = [];
          for (const v of variants) {
            next.push(v);
            for (let i = 2; i <= mod.count; i++) next.push(`${v}-${i}`);
          }
          variants = next;
        }
      }
      // Node-own suffixes apply FIRST, ancestor suffixes append after — matching
      // the compiler, where a socket picks up its own node's modifier suffix and
      // each ancestor's stack appends as the element bubbles up the tree.
      const combined = suffixes.flatMap((outer) => variants.map((inner) => inner + outer));
      if (node.kind === "socket" && node.name) {
        for (const suffix of combined) names.add(node.name + suffix);
      }
      if (node.children) expandChain(node.children, combined);
    }
  };
  expandChain(asset.nodes, [""]);
  return names;
}

function nodeIssues(node: unknown, path: string, ids: Set<string>, issues: string[], materialCount: number): void {
  if (typeof node !== "object" || node === null) { issues.push(`${path}: not an object`); return; }
  const n = node as AssetNode;
  if (typeof n.id !== "string" || !n.id) issues.push(`${path}: missing id`);
  else if (ids.has(n.id)) issues.push(`${path}: duplicate node id ${n.id}`);
  else ids.add(n.id);
  if (!ASSET_NODE_KINDS.includes(n.kind)) { issues.push(`${path}: unknown node kind ${String(n.kind)}`); return; }
  if (!vec3In(n.p, ASSET_POS_BOUND)) issues.push(`${path}: invalid position`);
  if (!vec3In(n.r, Math.PI * 4)) issues.push(`${path}: invalid rotation`);
  if (n.s !== undefined) {
    if (!vec3In(n.s, ASSET_SCALE_MAX) || n.s.some((v) => v < ASSET_SCALE_MIN)) issues.push(`${path}: invalid scale`);
  }
  if (n.faceting !== undefined && (n.kind !== 'lathe' || !finite(n.faceting) || n.faceting < 0 || n.faceting > 1)) {
    issues.push(`${path}: faceting must be 0..1 on a lathe`);
  }
  const paramCount = PRIMITIVE_PARAM_COUNTS[n.kind];
  if (paramCount !== undefined) {
    if (!Array.isArray(n.params) || n.params.length !== paramCount
      || !n.params.every((v) => finite(v) && v >= ASSET_DIM_MIN && v <= ASSET_DIM_MAX)) {
      issues.push(`${path}: ${n.kind} needs ${paramCount} dimension(s) in [${ASSET_DIM_MIN}, ${ASSET_DIM_MAX}]`);
    }
    if (n.kind === "lathe") {
      const segments = n.params?.[0];
      if (segments === undefined || !Number.isInteger(segments) || segments < 3 || segments > 48) {
        issues.push(`${path}: lathe segments must be an integer 3..48`);
      }
      if (!Array.isArray(n.profile) || n.profile.length < 2 || n.profile.length > ASSET_MAX_PROFILE_POINTS
        || !n.profile.every((pt) => Array.isArray(pt) && pt.length === 2
          && finite(pt[0]) && pt[0] >= 0 && pt[0] <= ASSET_DIM_MAX
          && finite(pt[1]) && Math.abs(pt[1]) <= ASSET_DIM_MAX)) {
        issues.push(`${path}: lathe needs 2..${ASSET_MAX_PROFILE_POINTS} profile points`);
      }
    } else if (n.profile !== undefined) issues.push(`${path}: profile is lathe-only`);
    if (typeof n.material !== "number" || !Number.isInteger(n.material)
      || n.material < 0 || n.material >= materialCount) {
      issues.push(`${path}: material index out of range`);
    }
  } else {
    if (n.params !== undefined) issues.push(`${path}: ${n.kind} takes no params`);
    if (n.material !== undefined) issues.push(`${path}: ${n.kind} takes no material`);
    if (n.profile !== undefined) issues.push(`${path}: profile is lathe-only`);
  }
  if (n.kind === "socket" && (typeof n.name !== "string" || !n.name)) {
    issues.push(`${path}: socket nodes require a name`);
  }
  if (n.modifiers !== undefined) {
    if (!Array.isArray(n.modifiers) || n.modifiers.length > ASSET_MAX_MODIFIERS) {
      issues.push(`${path}: at most ${ASSET_MAX_MODIFIERS} modifiers`);
    } else {
      n.modifiers.forEach((mod, index) => modifierIssues(mod, `${path}.modifiers[${index}]`, issues));
    }
  }
  if (n.children !== undefined) {
    if (!Array.isArray(n.children)) issues.push(`${path}: children must be an array`);
    else n.children.forEach((child, index) => nodeIssues(child, `${path}.children[${index}]`, ids, issues, materialCount));
  }
}

function modifierIssues(mod: unknown, path: string, issues: string[]): void {
  if (typeof mod !== "object" || mod === null) { issues.push(`${path}: not an object`); return; }
  const m = mod as AssetModifier;
  const axisOk = (axis: unknown): boolean => axis === "x" || axis === "y" || axis === "z";
  switch (m.type) {
    case "mirror":
      if (!axisOk(m.axis)) issues.push(`${path}: mirror axis`);
      break;
    case "array":
      if (!Number.isInteger(m.count) || m.count < 2 || m.count > ASSET_MAX_ARRAY_COUNT) {
        issues.push(`${path}: array count must be 2..${ASSET_MAX_ARRAY_COUNT}`);
      }
      if (m.mode === "linear") {
        if (!vec3In(m.offset, ASSET_POS_BOUND)) issues.push(`${path}: array offset`);
      } else if (m.mode === "radial") {
        if (!axisOk(m.axis)) issues.push(`${path}: array axis`);
        if (!finite(m.radius) || m.radius < 0 || m.radius > ASSET_POS_BOUND) issues.push(`${path}: array radius`);
        if (m.sweep !== undefined && (!finite(m.sweep) || m.sweep <= 0 || m.sweep > Math.PI * 2)) {
          issues.push(`${path}: array sweep must be (0, 2π]`);
        }
      } else issues.push(`${path}: unknown array mode`);
      break;
    case "taper":
      if (!axisOk(m.axis)) issues.push(`${path}: taper axis`);
      if (!finite(m.factor) || m.factor < 0 || m.factor > 4) issues.push(`${path}: taper factor must be 0..4`);
      break;
    case "bend":
      if (!axisOk(m.axis)) issues.push(`${path}: bend axis`);
      if (!finite(m.angle) || Math.abs(m.angle) > Math.PI) issues.push(`${path}: bend angle must be within ±π`);
      break;
    default:
      issues.push(`${path}: unknown modifier type`);
  }
}

/** Validate an untrusted document. Empty array = safe to compile and render. */
export function proceduralAssetIssues(value: unknown): string[] {
  const issues: string[] = [];
  if (typeof value !== "object" || value === null) return ["asset: not an object"];
  const a = value as ProceduralAssetV1;
  if (a.version !== 1) return ["asset: unsupported version"];
  if (typeof a.id !== "string" || !SLUG.test(a.id)) issues.push("asset: id must be a slug");
  if (typeof a.name !== "string" || !a.name.trim()) issues.push("asset: name required");
  if (!ASSET_KINDS.includes(a.kind)) issues.push("asset: unknown kind");
  if (!Array.isArray(a.tags) || !a.tags.every((tag) => typeof tag === "string")) issues.push("asset: tags must be strings");
  if (!Array.isArray(a.materials) || a.materials.length < 1 || a.materials.length > ASSET_MAX_MATERIALS) {
    issues.push(`asset: 1..${ASSET_MAX_MATERIALS} materials required`);
  } else {
    a.materials.forEach((mat, index) => {
      const ok = typeof mat === "object" && mat !== null
        && Number.isInteger(mat.color) && mat.color >= 0 && mat.color <= 0xffffff
        && finite(mat.roughness) && mat.roughness >= 0 && mat.roughness <= 1
        && finite(mat.metalness) && mat.metalness >= 0 && mat.metalness <= 1
        && (mat.emissive === undefined || (Number.isInteger(mat.emissive) && mat.emissive >= 0 && mat.emissive <= 0xffffff))
        && (mat.emissiveIntensity === undefined || (finite(mat.emissiveIntensity) && mat.emissiveIntensity >= 0 && mat.emissiveIntensity <= 8))
        && (mat.opacity === undefined || (finite(mat.opacity) && mat.opacity >= 0.05 && mat.opacity <= 1));
      if (!ok) issues.push(`materials[${index}]: invalid`);
    });
  }
  if (!Array.isArray(a.nodes) || a.nodes.length === 0) issues.push("asset: nodes required");
  else {
    const ids = new Set<string>();
    let nodeCount = 0, instanceCount = 0;
    walk(a.nodes, (node, multiplier) => {
      nodeCount++;
      if (node.kind !== "group") instanceCount += multiplier;
    });
    if (nodeCount > ASSET_MAX_NODES) issues.push(`asset: over ${ASSET_MAX_NODES} nodes`);
    if (instanceCount > ASSET_MAX_INSTANCES) issues.push(`asset: modifiers expand past ${ASSET_MAX_INSTANCES} instances`);
    const materialCount = Array.isArray(a.materials) ? a.materials.length : 0;
    a.nodes.forEach((node, index) => nodeIssues(node, `nodes[${index}]`, ids, issues, materialCount));
    // socket-name uniqueness across modifier expansion
    const seen = new Set<string>();
    const sockets: string[] = [];
    walk(a.nodes, (node) => { if (node.kind === "socket" && node.name) sockets.push(node.name); });
    for (const name of sockets) {
      if (seen.has(name)) issues.push(`asset: duplicate socket name ${name}`);
      seen.add(name);
    }
  }
  const blocks = [a.vehicle !== undefined, a.item !== undefined, a.ship !== undefined, a.buildingModule !== undefined, a.buildPiece !== undefined].filter(Boolean).length;
  if (blocks !== 1) issues.push("asset: exactly one kind block required");
  else if ((a.kind === "vehicle") !== (a.vehicle !== undefined)
    || (a.kind === "item") !== (a.item !== undefined)
    || (a.kind === "ship") !== (a.ship !== undefined)
    || (a.kind === "buildingModule") !== (a.buildingModule !== undefined)
    || (a.kind === "buildPiece") !== (a.buildPiece !== undefined)) {
    issues.push("asset: kind block must match kind");
  }
  if (a.vehicle) vehicleBlockIssues(a, issues);
  if (a.item) {
    const item = a.item;
    if (!["weapon", "ammo", "consumable", "material", "module"].includes(item.kind)) issues.push("item: unknown kind");
    if (!Number.isInteger(item.stack) || item.stack < 1 || item.stack > 999) issues.push("item: stack must be 1..999");
    if (typeof item.desc !== "string") issues.push("item: desc required");
    if (item.heal !== undefined && (!finite(item.heal) || item.heal <= 0)) issues.push("item: invalid heal");
    if (item.shield !== undefined && (!finite(item.shield) || item.shield <= 0)) issues.push("item: invalid shield");
  }
  if (a.ship) {
    const groups = a.ship.weaponGroups;
    if (typeof groups !== "object" || groups === null
      || !Object.values(groups).every((g) => g === 1 || g === 2)) {
      issues.push("ship: weaponGroups must map socket names to 1|2");
    }
  }
  if (a.buildingModule) {
    const fp = a.buildingModule.footprintUnits;
    if (!Array.isArray(fp) || fp.length !== 2
      || !fp.every((n) => Number.isInteger(n) && n >= 1 && n <= 5)) {
      issues.push("buildingModule: footprintUnits must be two integers 1..5");
    }
    walk(a.nodes, (node) => {
      if (node.kind === "socket" && node.name
        && !BUILDING_SOCKET_PREFIXES.some((p) => node.name!.startsWith(p))) {
        issues.push(`buildingModule: socket ${node.name} must be prefixed ${BUILDING_SOCKET_PREFIXES.join("|")}`);
      }
    });
  }
  if (a.buildPiece) {
    if (!(BUILD_PIECE_GROUPS as readonly string[]).includes(a.buildPiece.group)) {
      issues.push(`buildPiece: group must be one of ${BUILD_PIECE_GROUPS.join("|")}`);
    }
    walk(a.nodes, (node) => {
      if (node.kind === "socket" && node.name
        && !BUILD_PIECE_SOCKET_PREFIXES.some((p) => node.name!.startsWith(p))) {
        issues.push(`buildPiece: socket ${node.name} must be prefixed ${BUILD_PIECE_SOCKET_PREFIXES.join("|")}`);
      }
    });
  }
  return issues;
}

function vehicleBlockIssues(asset: ProceduralAssetV1, issues: string[]): void {
  const v = asset.vehicle!;
  const sockets = assetSocketNames(asset);
  const needSocket = (name: unknown, label: string): void => {
    if (typeof name !== "string" || !sockets.has(name)) issues.push(`vehicle: ${label} references unknown socket ${String(name)}`);
  };
  if (!["hover", "wheel", "track"].includes(v.drive)) issues.push("vehicle: unknown drive");
  if (!finite(v.mass) || v.mass <= 0) issues.push("vehicle: invalid mass");
  if (!vec3In(v.inertia, 1e6) || v.inertia.some((n) => n <= 0)) issues.push("vehicle: invalid inertia");
  if (!Array.isArray(v.probes) || v.probes.length < 3 || v.probes.length > 8) issues.push("vehicle: 3..8 probes required");
  else for (const probe of v.probes) {
    if (!vec3In(probe.position, ASSET_POS_BOUND) || !finite(probe.rideHeight) || probe.rideHeight <= 0
      || !finite(probe.travel) || probe.travel <= 0 || !finite(probe.spring) || probe.spring <= 0
      || !finite(probe.damping) || probe.damping <= 0) issues.push("vehicle: invalid probe");
  }
  if (typeof v.handling !== "object" || v.handling === null
    || !Object.values(v.handling).every((n) => finite(n))) issues.push("vehicle: invalid handling");
  if (typeof v.collider !== "object" || v.collider === null
    || !finite(v.collider.radius) || v.collider.radius <= 0
    || !finite(v.collider.halfHeight) || v.collider.halfHeight <= 0) issues.push("vehicle: invalid collider");
  if (typeof v.camera !== "object" || v.camera === null
    || !Object.values(v.camera).every((n) => finite(n))) issues.push("vehicle: invalid camera");
  if (!Array.isArray(v.seats) || v.seats.length === 0) issues.push("vehicle: seats required");
  else {
    const seatIds = new Set<string>();
    let drivers = 0;
    for (const seat of v.seats) {
      if (!seat.id || seatIds.has(seat.id)) issues.push(`vehicle: duplicate seat ${seat.id}`);
      seatIds.add(seat.id);
      if (seat.role === "driver") drivers++;
      needSocket(seat.seatSocket, `seat ${seat.id} seatSocket`);
      needSocket(seat.exitSocket, `seat ${seat.id} exitSocket`);
      if (seat.entrySocket !== undefined) needSocket(seat.entrySocket, `seat ${seat.id} entrySocket`);
    }
    if (drivers !== 1) issues.push(`vehicle: exactly one driver seat required (found ${drivers})`);
  }
  if (!Array.isArray(v.weapons)) issues.push("vehicle: weapons must be an array");
  if (!Array.isArray(v.hardpoints)) issues.push("vehicle: hardpoints must be an array");
  else for (const hp of v.hardpoints) {
    needSocket(hp.socket, "hardpoint");
    if (hp.kind !== "weapon" && hp.kind !== "utility") issues.push("vehicle: hardpoint kind");
    if (hp.kind === "weapon" && hp.group !== 1 && hp.group !== 2) issues.push(`vehicle: weapon hardpoint ${hp.socket} needs a fire group`);
    if (hp.kind === "weapon" && (typeof hp.weapon !== "string"
      || !(Array.isArray(v.weapons) && v.weapons.some((w) => w.id === hp.weapon)))) {
      issues.push(`vehicle: weapon hardpoint ${hp.socket} needs a known weapon`);
    }
  }
}
